import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DownloadIcon from "@mui/icons-material/Download";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  IconButton,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import { useState } from "react";
import { StyledInfoEquipment } from "./styles";
import { downloadCertificate } from "src/services/certificates";

interface Equipment {
  id?: number;
  name: string;
  brand: string;
  model: string;
  serial_number: string;
  technician: string;
  calibration_date: string;
  expiration_date: string;
  public_hash?: string;
}

interface AccordionComponentProps {
  equipments: Equipment[];
  onDelete: (equipmentId: number) => void;
}

export default function AccordionComponent({
  equipments,
  onDelete,
}: AccordionComponentProps) {
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const onlyDate = dateString.split("T")[0];
    const [year, month, day] = onlyDate.split("-");
    return `${day}/${month}/${year}`;
  };

  const isExpired = (expirationDate: string) => {
    const today = new Date();
    const exp = new Date(expirationDate);
    return exp.getTime() < today.setHours(0, 0, 0, 0);
  };

  const handleDownloadCertificate = async (publicHash?: string) => {
    if (!publicHash) {
      alert("Certificado não disponível para este equipamento.");
      return;
    }

    try {
      setLoadingId(publicHash);

      const response = await downloadCertificate(publicHash);
      if (response) {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `certificado-${publicHash}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Erro ao baixar certificado:", error);
      alert("Erro ao baixar o certificado. Tente novamente.");
    } finally {
      setLoadingId(null);
    }
  };


  const handleDeleteWithCheck = (id?: number) => {
    if (!id) return;

    const now = new Date();
    const day = now.getDate();
    const month = now.getMonth() + 1;
    const minute = now.getMinutes();

    const minuteStr = String(minute).padStart(2, "0");
    const chave = `${minuteStr}${day * month}`;

    const input = prompt(
      "⚙️ Para confirmar a exclusão do equipamento, digite a chave de acesso:"
    );

    if (String(input) !== String(chave)) {
      alert("❌ Senha incorreta! Exclusão não permitida.");
      return;
    }

    if (!window.confirm("Tem certeza que deseja excluir este equipamento?")) {
      return;
    }

    onDelete(id);
  };

  const sortedEquipments = [...equipments].sort((a, b) => {
    const dateA = new Date(a.expiration_date).getTime();
    const dateB = new Date(b.expiration_date).getTime();
    return dateA - dateB;
  });

  return (
    <div>
      {sortedEquipments.map((eq, index) => {
        const expired = isExpired(eq.expiration_date);

        return (
          <Accordion
            key={index}
            sx={{
              "&:before": { display: "none" },
              marginBottom: "8px",
              borderRadius: 2,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: "#ffffff" }} />}
              id={`panel-${eq.id || index}-header`}
              component="div"
              sx={(theme) => ({
                backgroundColor: expired ? "#d32f2f" : "#0B3095",
                borderRadius: 2,
                color: "#ffffff",
                minHeight: 40,
                "&.Mui-expanded": { minHeight: 40 },
                [theme.breakpoints.down("sm")]: {
                  minHeight: "auto",
                  paddingY: 1,
                },
              })}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: "100%" }}
              >
                <StyledInfoEquipment as="span">
                  {eq.name} — {eq.serial_number} —{" "}
                  <strong style={{ color: "#ffffff" }}>
                    Válido até: {formatDate(eq.expiration_date)}
                  </strong>
                </StyledInfoEquipment>

                <Stack direction="row" alignItems="center" spacing={1}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadCertificate(eq.public_hash);
                    }}
                    sx={{
                      color: "#fff",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }}
                    disabled={loadingId === eq.public_hash}
                  >
                    {loadingId === eq.public_hash ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <DownloadIcon fontSize="small" />
                    )}
                  </IconButton>

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteWithCheck(eq.id);
                    }}
                    sx={{
                      color: "#fff",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }}
                    disabled={loadingId !== null}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
                textTransform: "uppercase",
                paddingLeft: 2,
              }}
            >
              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoEquipment as="span">Equipamento:</StyledInfoEquipment> {eq.name}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoEquipment as="span">Marca:</StyledInfoEquipment> {eq.brand}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoEquipment as="span">Modelo:</StyledInfoEquipment> {eq.model}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoEquipment as="span">Nº de série:</StyledInfoEquipment> {eq.serial_number}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoEquipment as="span">Técnico:</StyledInfoEquipment> {eq.technician}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoEquipment as="span">Data de calibração:</StyledInfoEquipment>{" "}
                {formatDate(eq.calibration_date)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  color: expired ? "#d32f2f" : "inherit",
                  fontWeight: expired ? 600 : "normal",
                }}
              >
                <StyledInfoEquipment as="span">Válido até:</StyledInfoEquipment>{" "}
                {formatDate(eq.expiration_date)} {expired && "— VENCIDO"}
              </Typography>
              <Typography sx={{ fontSize: 14 }}>
                <StyledInfoEquipment as="span">Hash:</StyledInfoEquipment> {eq.public_hash}
              </Typography>

              <Button
                variant="contained"
                color={expired ? "error" : "primary"}
                startIcon={
                  loadingId === eq.public_hash ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={() => handleDownloadCertificate(eq.public_hash)}
                sx={{ mt: 1, width: "fit-content" }}
                disabled={loadingId === eq.public_hash}
              >
                {loadingId === eq.public_hash ? "Baixando..." : "Baixar Certificado"}
              </Button>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
}
